/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'
import { upperFirst } from 'lodash'

import {
  EuiCheckableCard,
  EuiFlexGroup,
  EuiIcon,
  EuiListGroup,
  EuiListGroupItem,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import type { SolutionType } from './types'
import type { FunctionComponent, ReactNode } from 'react'

type Props = {
  label: SolutionType
  description: ReactNode
  features: ReactNode[]
  logo: string
  title: ReactNode
  onNext: (label: SolutionType) => void
  buttonDisabled?: boolean
  dataAttributePrefix: string
  checked?: boolean
}

const SelectSolutionCardV6: FunctionComponent<Props> = ({
  label,
  description,
  features,
  logo,
  title,
  onNext,
  checked = false,
}) => {
  const radioGroupId = useGeneratedHtmlId({ prefix: 'radioGroup' })

  return (
    <EuiCheckableCard
      name={radioGroupId}
      onChange={() => onNext(label)}
      id={`${radioGroupId}${label}`}
      checked={checked}
      label={
        <EuiFlexGroup alignItems='center' justifyContent='center' gutterSize='xl'>
          <EuiTitle size='xs'>
            <p>{title}</p>
          </EuiTitle>
          <EuiFlexGroup alignItems='center' justifyContent='flexEnd' gutterSize='xs'>
            <EuiIcon size='s' type={logo ? logo : `logo${upperFirst(label)}`} />
            <EuiTitle size='xxxs'>
              <h2>{upperFirst(label)}</h2>
            </EuiTitle>
          </EuiFlexGroup>
        </EuiFlexGroup>
      }
      css={{
        width: '630px',
      }}
    >
      <EuiFlexGroup direction='column' gutterSize='xxs'>
        <EuiListGroup style={{ marginLeft: -15, marginTop: -10 }} maxWidth='65ch' size='s'>
          {features.map((desc, key) => (
            <EuiListGroupItem
              wrapText={true}
              label={desc}
              iconType='dot'
              iconProps={{
                size: 's',
                css: {
                  marginRight: '8px',
                },
              }}
              key={key}
              color='subdued'
              css={{
                marginTop: '-15px',
              }}
            />
          ))}
        </EuiListGroup>
        <EuiText size='s' color='subdued'>
          <p>{description}</p>
        </EuiText>
      </EuiFlexGroup>
    </EuiCheckableCard>
  )
}

export default SelectSolutionCardV6
